import React from 'react'
import { FormMultiSelectField, FormSelectField } from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Form, Formik, FormikHelpers } from 'formik'
import * as Yup from 'yup'

import { CountryDto } from '../../../api/Identity/Types/Country'
import { NPaymentProviderMethodConfiguration } from '../../../api/NPayments/Types/NPaymentProviderDto'
import { WalletCurrencyDto } from '../../../api/Wallet/Types/WalletCurrencyDto'
import { WalletTypeDto } from '../../../api/Wallet/Types/WalletTypeDto'

interface NPaymentProviderMethodConfigurationFormValues {
  id?: string
  walletTypeId?: number
  countryId?: string
  currencyId?: string
  countryIds?: string[]
}

interface MethodDefaultFormProps {
  open: boolean
  v: NPaymentProviderMethodConfigurationFormValues
  onSubmit: (
    values: NPaymentProviderMethodConfiguration & { countries: CountryDto[] },
    actions: FormikHelpers<NPaymentProviderMethodConfigurationFormValues>
  ) => void
  onClose: () => void
  walletType: WalletTypeDto[]
  countries: CountryDto[]
  walletCurrencies: WalletCurrencyDto[]
  existingConfigurations?: {
    id?: string
    walletTypeId: number
    currencyId: string
    countryId: string
  }[]
}

const validationSchema = Yup.object().shape({
  walletTypeId: Yup.string().required('Wallet Type is required'),
  currencyId: Yup.string().required('Currency is required'),
  countryId: Yup.string().when('id', {
    is: (id: string | undefined) => !!id,
    then: () => Yup.string().required('Country is required'),
    otherwise: () => Yup.string().nullable(),
  }),
  countryIds: Yup.array().when('id', {
    is: (id: string | undefined) => !id,
    then: () =>
      Yup.array()
        .of(Yup.string())
        .min(1, 'At least one country must be selected')
        .required('Countries are required'),
    otherwise: () => Yup.array().nullable(),
  }),
})

const PaymentProviderMethodConfigurationForm: React.FC<MethodDefaultFormProps> = ({
  open,
  v,
  onSubmit,
  onClose,
  walletType,
  walletCurrencies,
  countries,
  existingConfigurations = [],
}) => {
  const initialValues: NPaymentProviderMethodConfigurationFormValues = {
    ...v,
    countryIds: [],
  }

  const dataToSubmit = (
    v: NPaymentProviderMethodConfigurationFormValues
  ): NPaymentProviderMethodConfiguration & { countries: CountryDto[] } => {
    let walletTypeDto = walletType.find((w) => w.id === Number(v.walletTypeId))
    let walletCurrency = walletCurrencies.find((c) => c.id === v.currencyId)
    let country = countries.find((d) => d.id.toString() === v.countryId?.toString())
    const countryDtos = v.countryIds
      ?.map((id) => countries.find((d) => d.id.toString() === id.toString()))
      .filter((countryDto): countryDto is CountryDto => Boolean(countryDto))
    if (!walletTypeDto) {
      walletTypeDto = walletType[0]
    }
    if (!walletCurrency) {
      walletCurrency = walletCurrencies[0]
    }
    if (!country && v.id) {
      country = countries[0]
    }
    return {
      id: v.id,
      walletType: walletTypeDto,
      walletCurrency: walletCurrency,
      country,
      countries: countryDtos ?? [],
    }
  }

  const isEdit = !!initialValues.id
  const walletCurrencyOptions = walletCurrencies.map((currency) => ({
    id: currency.id,
    name: currency.id,
  }))
  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(values, formikHelpers) => onSubmit(dataToSubmit(values), formikHelpers)}
    >
      {({ handleSubmit, isSubmitting, resetForm, values, setFieldValue }) => {
        const handleClose = () => {
          resetForm()
          onClose()
        }

        // A country is unavailable only for the currently selected
        // wallet type + currency pair — the same country can be added
        // again with a different combination.
        const countryOptions = countries.map((country) => ({
          ...country,
          disabled: existingConfigurations.some(
            (config) =>
              config.id !== values.id &&
              config.walletTypeId === values.walletTypeId &&
              config.currencyId === values.currencyId &&
              config.countryId === country.id
          ),
        }))

        return (
          <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && handleClose()}>
            <DialogContent size='sm' render={<Form />}>
              <DialogHeader>
                <DialogTitle>{isEdit ? 'Edit' : 'Add New Country'}</DialogTitle>
              </DialogHeader>

              <FormSelectField
                name='walletTypeId'
                label='Wallet Type'
                options={walletType}
                value={values.walletTypeId}
                allowClear
                showSearch
                onChange={(value) => setFieldValue('walletTypeId', value ? Number(value) : '')}
              />
              <FormSelectField
                name='currencyId'
                label='Currency'
                options={walletCurrencyOptions}
                value={values.currencyId}
                allowClear
                showSearch
                onChange={(value) => setFieldValue('currencyId', value ? String(value) : '')}
              />
              {isEdit ? (
                <FormSelectField
                  name='countryId'
                  label='Country'
                  options={countryOptions}
                  value={values.countryId}
                  allowClear
                  showSearch
                  onChange={(value) => setFieldValue('countryId', value ? String(value) : '')}
                />
              ) : (
                <FormMultiSelectField
                  name='countryIds'
                  label='Countries'
                  options={countryOptions}
                  value={values.countryIds ?? []}
                  placeholder='Countries'
                  selectionSummaryThreshold={5}
                  showSearch
                  onChange={(selectedIds) => {
                    setFieldValue(
                      'countryIds',
                      Array.isArray(selectedIds) ? selectedIds.map(String) : []
                    )
                  }}
                />
              )}

              <DialogFooter>
                <Button variant='outline' type='button' onClick={handleClose}>
                  Cancel
                </Button>
                <Button
                  disabled={isSubmitting}
                  key='button'
                  type='button'
                  onClick={() => handleSubmit()}
                >
                  Save
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )
      }}
    </Formik>
  )
}

export default PaymentProviderMethodConfigurationForm
